// useTodoTags — user-managed tag set for the /todos surface. The server
// auto-seeds the legacy 7 defaults on first load for a user who has
// never used tags, so this hook can be mounted unconditionally.
//
// Mutations are optimistic: create / rename / delete update local state
// immediately, then reconcile against the server response. On error we
// re-fetch the canonical list (cheap — usually <20 tags) and surface
// the message via the caller's notify so the user sees what failed.

import { useCallback, useEffect, useState } from "preact/hooks";
import { api, type TodoTag } from "./rpc";

export interface UseTodoTags {
  tags: TodoTag[];
  loading: boolean;
  error: string | null;
  create: (name: string) => Promise<TodoTag | null>;
  rename: (tagId: number, name: string) => Promise<TodoTag | null>;
  remove: (tagId: number) => Promise<number>;
  reorder: (ids: number[]) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useTodoTags(): UseTodoTags {
  const [tags, setTags] = useState<TodoTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const next = await api.todoTagsList();
      setTags(next);
      setError(null);
    } catch (err) {
      setError((err as Error)?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const create = useCallback(async (name: string): Promise<TodoTag | null> => {
    try {
      const tag = await api.todoTagCreate(name);
      setTags((prev) => [...prev, tag].sort((a, b) => a.sortOrder - b.sortOrder));
      return tag;
    } catch (err) {
      setError((err as Error)?.message || String(err));
      return null;
    }
  }, []);

  const rename = useCallback(async (tagId: number, name: string): Promise<TodoTag | null> => {
    try {
      const tag = await api.todoTagRename(tagId, name);
      setTags((prev) => prev.map((t) => (t.id === tagId ? tag : t)));
      return tag;
    } catch (err) {
      setError((err as Error)?.message || String(err));
      await refresh();
      return null;
    }
  }, [refresh]);

  const remove = useCallback(async (tagId: number): Promise<number> => {
    // Optimistic drop. If the server says no, the catch refreshes from
    // truth so the row pops back in.
    const before = tags;
    setTags((prev) => prev.filter((t) => t.id !== tagId));
    try {
      const r = await api.todoTagDelete(tagId);
      return r.affectedItems;
    } catch (err) {
      setError((err as Error)?.message || String(err));
      setTags(before);
      return 0;
    }
  }, [tags]);

  const reorder = useCallback(async (ids: number[]): Promise<void> => {
    // Local reorder first (the manage sheet wants instant feedback);
    // server returns the canonical list afterward.
    setTags((prev) => {
      const byId = new Map(prev.map((t) => [t.id, t]));
      const next: TodoTag[] = [];
      ids.forEach((id, i) => {
        const t = byId.get(id);
        if (t) next.push({ ...t, sortOrder: i });
      });
      // Preserve any tag whose id wasn't passed in (shouldn't happen
      // in normal use but keeps the UI from blanking on a bug).
      for (const t of prev) if (!ids.includes(t.id)) next.push(t);
      return next;
    });
    try {
      const fresh = await api.todoTagReorder(ids);
      setTags(fresh);
    } catch (err) {
      setError((err as Error)?.message || String(err));
      await refresh();
    }
  }, [refresh]);

  return { tags, loading, error, create, rename, remove, reorder, refresh };
}
